/* eslint-disable */
/*
This script is meant to be run in browser's console

Our inventory meter is wounded and it needs to be patched! Quick!
Let's give him a proper treatment by giving him values he needs!

USAGE:
  Simply open console tab in your browser and start typing IndexedDbTools
  You can choose between these modes:

    checkForNotSynced() {
      will give you a list of unsynced meters which needs to be updated
    }

    updateByIdWithData(inventoryMeterObjectStoreId: string, newData: {}) {
      first argument is id of the inventory meter object store
      which has this format b7f566a4-2595-4812-9562-bbfd0fd58e0f

      second argument is object with properties with new values

      let's say our inventory meter has property is_direct with undefined value

      To change that, simply pass this object as a second argument -> { is_direct: true, }
    }

    extractAllData() {
      🎯 RECOMMENDED: Creates a ZIP file with organized folders for each inventory meter

      Each meter folder (named by meter.name) contains:
      ├── context/ (photos from originPhotoContextList)
      ├── detail/ (photos from originPhotoDetailList)
      ├── register_reading/ (photos from originPhotoRegisterReadingList)
      ├── meter_module/ (photos from originPhotoMeterModuleList)
      └── inventory_meter_data.json (complete record from IndexedDB)

      Requires JSZip library. Add this to your page first:
      <script src="https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js"></script>

      Example: IndexedDbTools.extractAllData()
    }
*/

class IndexedDbTools {
  static DB_NAME = 'inventory_meters'; // Default database name
  static DB_VERSION = 1;
  static OBJECT_STORE_NAME = this.DB_NAME;

  static async #openDatabase() {
    return new Promise((resolve, reject) => {
      const dbConnection = window.indexedDB.open(this.DB_NAME, this.DB_VERSION);

      dbConnection.onsuccess = e => resolve(e.target.result);
      dbConnection.onerror = e => reject(e.target.error);
    });
  }

  static async checkForNotSynced() {
    try {
      const db = await this.#openDatabase();
      const transaction = db.transaction([this.OBJECT_STORE_NAME], 'readonly');
      const objectStore = transaction.objectStore(this.OBJECT_STORE_NAME);

      return new Promise((resolve, reject) => {
        objectStore.getAll().onsuccess = e => {
          const unsyncedMeters = e.target.result.filter(objectStore => objectStore.wasSynced === 0);

          if (unsyncedMeters.length === 0) {
            console.log('✅ All meters are synced');
          } else {
            console.log(`❌ Found ${unsyncedMeters.length} unsynced meters:`);
            unsyncedMeters.forEach((meter, index) => {
              console.log(`${index + 1}.`, meter);
            });
          }
          resolve(unsyncedMeters);
        };
        objectStore.getAll().onerror = e => reject(e.target.error);
      });
    } catch (error) {
      console.error('❌ Database error:', error);
    }
  }

  static async updateByIdWithData(inventoryMeterID, editedData = {}) {
    try {
      const db = await this.#openDatabase();
      const transaction = db.transaction([this.OBJECT_STORE_NAME], 'readwrite');
      const objectStore = transaction.objectStore(this.OBJECT_STORE_NAME);
      const index = objectStore.index('id');

      return new Promise((resolve, reject) => {
        const getRequest = index.get(inventoryMeterID);

        getRequest.onsuccess = e => {
          const imosObject = e.target.result;

          if (!imosObject) {
            console.error(`❌ Record with ID ${inventoryMeterID} not found`);
            reject(new Error('Record not found'));
            return;
          }

          const originalData = imosObject.inventoryMeter?.data || {};
          const originalKeyCount = Object.keys(originalData).length;

          const updatedItem = {
            ...imosObject,
            id: inventoryMeterID,
            inventoryMeter: {
              ...imosObject.inventoryMeter,
              data: {
                ...originalData,
                ...editedData,
              },
            },
          };

          const putRequest = objectStore.put(updatedItem);

          putRequest.onsuccess = () => {
            const verifyRequest = index.get(inventoryMeterID);
            verifyRequest.onsuccess = e => {
              const updatedRecord = e.target.result;
              const newKeyCount = Object.keys(updatedRecord.inventoryMeter.data).length;

              if (newKeyCount === originalKeyCount) {
                console.log('✅ UPDATE WAS SUCCESSFUL!');
              } else {
                console.warn(`⚠️ New properties were added:\n${JSON.stringify(editedData, null, 2)}`);
              }
              resolve(updatedRecord);
            };
            verifyRequest.onerror = e => reject(e.target.error);
          };

          putRequest.onerror = e => reject(e.target.error);
        };

        getRequest.onerror = e => reject(e.target.error);
      });
    } catch (error) {
      console.error('❌ Database error:', error);
    }
  }

  static async extractAllData() {
    if (typeof JSZip === 'undefined') {
      console.error('❌ JSZip library not found. Please include JSZip to use this feature.');
      console.log(
        '💡 Add this to your page: <script src="https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js"></script>',
      );
      return;
    }

    try {
      const db = await this.#openDatabase();
      const transaction = db.transaction([this.OBJECT_STORE_NAME], 'readonly');
      const objectStore = transaction.objectStore(this.OBJECT_STORE_NAME);

      return new Promise((resolve, reject) => {
        objectStore.getAll().onsuccess = async e => {
          try {
            const allData = e.target.result;
            const zip = new JSZip();
            let processedRecords = 0;
            let totalPhotos = 0;

            console.log(`📦 Creating ZIP file with photos from ${allData.length} records...`);

            for (const item of allData) {
              const result = await this.#processRecord(item, zip);
              if (result.processed) {
                processedRecords++;
                totalPhotos += result.photoCount;
              }
            }

            if (processedRecords > 0) {
              await this.#generateAndDownloadZip(zip, processedRecords, totalPhotos);
              resolve({ processedRecords, totalPhotos });
            } else {
              console.log('❌ No records with photos found');
              resolve({ processedRecords: 0, totalPhotos: 0 });
            }
          } catch (error) {
            reject(error);
          }
        };
        objectStore.getAll().onerror = e => reject(e.target.error);
      });
    } catch (error) {
      console.error('❌ Database error:', error);
    }
  }

  static async #processRecord(item, zip) {
    const recordId = item.id;
    const formPhotos = item.inventoryMeter?.formPhotos;
    const meterData = item.inventoryMeter?.data || {};
    const meterName = meterData.monitored_entity || `meter_${recordId.slice(0, 8)}`;

    console.log(`📋 Processing meter: "${meterName}" (${recordId})`);

    const sanitizedMeterName = meterName.replace(/[<>:"/\\|?*]/g, '_');
    const recordFolder = zip.folder(sanitizedMeterName);

    let recordHasPhotos = false;
    let photoCount = 0;

    if (formPhotos && typeof formPhotos === 'object') {
      const folderMap = {
        originPhotoContextList: recordFolder.folder('context'),
        originPhotoDetailList: recordFolder.folder('detail'),
        originPhotoRegisterReadingList: recordFolder.folder('register_reading'),
        originPhotoMeterModuleList: recordFolder.folder('meter_module'),
      };

      for (const [photoKey, photoArray] of Object.entries(formPhotos)) {
        if (Array.isArray(photoArray) && photoArray.length > 0) {
          console.log(`📸 Processing ${photoArray.length} photos for section '${photoKey}' in meter "${meterName}"`);

          const targetFolder = folderMap[photoKey] || recordFolder;
          const processedPhotos = await this.#processPhotos(photoArray, targetFolder);

          if (processedPhotos > 0) {
            recordHasPhotos = true;
            photoCount += processedPhotos;
          }
        }
      }
    }

    // Create meter data JSON
    const fullMeterData = {
      extractionInfo: {
        extractedAt: new Date().toISOString(),
        meterName: meterName,
        recordId: recordId,
        photoSections: Object.keys(formPhotos || {}),
        totalPhotos: photoCount,
      },
      completeRecord: item,
    };

    recordFolder.file('inventory_meter_data.json', JSON.stringify(fullMeterData, null, 2));
    console.log(`✅ Meter "${meterName}": Added to ZIP with ${recordHasPhotos ? 'photos' : 'data only'}`);

    return { processed: true, photoCount };
  }

  static async #processPhotos(photoArray, targetFolder) {
    let processedCount = 0;

    for (let i = 0; i < photoArray.length; i++) {
      const photoItem = photoArray[i];

      // Handle nested arrays (like in register_reading)
      if (Array.isArray(photoItem)) {
        for (let j = 0; j < photoItem.length; j++) {
          const nestedPhotoFile = photoItem[j];
          if (nestedPhotoFile && typeof nestedPhotoFile === 'object') {
            const processed = await this.#processPhotoFile(nestedPhotoFile, targetFolder, `${i}_${j}`);
            if (processed) processedCount++;
          }
        }
      } else if (photoItem && typeof photoItem === 'object') {
        const processed = await this.#processPhotoFile(photoItem, targetFolder, i);
        if (processed) processedCount++;
      }
    }

    return processedCount;
  }

  static async #processPhotoFile(photoFile, targetFolder, index) {
    try {
      let photoData = null;
      let fileName = photoFile.name || `photo_${index + 1}`;

      if (!fileName.includes('.')) {
        const fileType = photoFile.type || 'image/jpeg';
        const extension = fileType.split('/')[1] || 'jpg';
        fileName += `.${extension}`;
      }

      if (photoFile instanceof File || photoFile instanceof Blob) {
        photoData = await new Promise(resolve => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = () => resolve(null);
          reader.readAsArrayBuffer(photoFile);
        });
      } else if (typeof photoFile === 'string' && photoFile.startsWith('data:')) {
        const base64Data = photoFile.split(',')[1];
        photoData = Uint8Array.from(atob(base64Data), c => c.charCodeAt(0));
      }

      if (photoData) {
        targetFolder.file(fileName, photoData);
        return true;
      }
    } catch (error) {
      console.error(`❌ Error processing photo ${index}:`, error);
    }

    return false;
  }

  static async #generateAndDownloadZip(zip, processedRecords, totalPhotos) {
    console.log(`📦 Generating ZIP file with ${processedRecords} records and ${totalPhotos} photos...`);

    try {
      const content = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_meters_photos_${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      console.log(`🎉 ZIP file downloaded successfully!`);
      console.log(`📊 Summary: ${processedRecords} records, ${totalPhotos} photos`);
    } catch (error) {
      console.error('❌ Error generating ZIP file:', error);
      throw error;
    }
  }
}

(function (window) {
  window.IndexedDbTools = IndexedDbTools;
})(window);
